// src/app/learn/LearnClient.tsx
//
// The /learn hub — the learning path view.
//
// Two tracks (Math, Reading & Writing) → the taxonomy's domains → the steps of
// src/lib/learn/learning-path.ts, each with the student's state from
// src/lib/learn/path-progress.ts: Locked / Available / In progress / Passed.
// One "Continue" CTA at the top points at the next step — the highest-leverage
// one for Pro, path order for everyone else.
//
// The display order is always the static path order (a path has a shape; the
// prerequisites only make sense read in sequence). Personalisation changes
// which step "Continue" targets, never where the cards sit.
//
// History: until 2026-09-09 this was a card grid sorted by two hand-picked
// "recommended" rails (10 of the 20 Math topics, no prerequisites, no gate)
// with an alphabetical "More topics" tail and a videos-only card type for
// topics without a lesson. Every canonical topic has a lesson now, so the
// videos-only card and its modal player are gone; the 49 sub-topic lessons
// hang off their step as "go deeper" links.

'use client'

import { useState, useMemo, useEffect } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  CalendarClock,
  CheckCircle2,
  Clock,
  Flag,
  Layers,
  Lock,
  PlayCircle,
  RotateCcw,
  Search,
  Sparkles,
  Target,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { MasteryMedal } from '@/components/MasteryMedal'
import VerifiedBadge from '@/components/lesson/VerifiedBadge'
import FirstStepCard from '@/components/FirstStepCard'
import ProveItButton from '@/components/lesson/ProveItButton'
import { unlocksIfPassed } from '@/lib/learn/placement-check'
import type { FirstStep } from '@/lib/onboarding/first-step'
import { trackPathEvent } from '@/lib/analytics/track-path-event'
import {
  LEARNING_PATH,
  checkpointHref,
  videoFor,
  type LearningPathStep,
  type LearningPathTrack,
} from '@/lib/learn/learning-path'
import {
  PATH_STATE_LABEL,
  gateThresholdPct,
  type PathNext,
  type PathProgress,
  type PathStepProgress,
} from '@/lib/learn/path-progress'
import type { HubTopic } from '@/lib/study-plan/learn-hub'
import { TIER_ACCENT } from '@/lib/scoring/mastery-display'
import { DOMAIN_QUESTIONS_PER_TEST, MASTERY_DISPLAY } from '@/lib/test-engine/sat-domains'

type SectionFilter = 'ALL' | 'RW' | 'MATH'

const DIFFICULTY_LABEL: Record<string, string> = {
  intro: 'Intro',
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
}

const DIFFICULTY_COLOR: Record<string, string> = {
  intro: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
  easy: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  hard: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
}

/* ------------------------------------------------------------------ */
/*  Step card                                                          */
/* ------------------------------------------------------------------ */

/** The numbered bubble on the left of a step — the state at a glance. */
function StepBubble({ n, progress }: { n: number; progress: PathStepProgress }) {
  if (progress.state === 'passed' && progress.passedBy === 'placement') {
    // Placed out: no medal (a tier needs a skill check's worth of answers).
    return (
      <span
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-emerald-500/50 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
        aria-label="Passed by placement"
      >
        <BadgeCheck className="h-4 w-4" aria-hidden="true" />
      </span>
    )
  }
  if (progress.state === 'passed') {
    return <MasteryMedal tier={progress.tier} className="h-8 w-8" />
  }
  if (progress.state === 'locked') {
    return (
      <span
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-muted-foreground"
        aria-label="Locked"
      >
        <Lock className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    )
  }
  const cls =
    progress.state === 'in_progress'
      ? 'border-brand/60 bg-brand/30 text-brand-ink'
      : 'border-border bg-card text-foreground'
  return (
    <span
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${cls}`}
    >
      {n}
    </span>
  )
}

function StateBadge({
  progress,
  isNext,
  firstStepPending,
}: {
  progress: PathStepProgress
  isNext: boolean
  /** A first step (FirstStepCard) is showing above the path under its own "Start here". */
  firstStepPending: boolean
}) {
  const base = 'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px]'
  switch (progress.state) {
    case 'passed':
      return (
        <span className={`${base} bg-emerald-500/15 text-emerald-700 dark:text-emerald-300`}>
          <CheckCircle2 className="h-3 w-3" />
          {PATH_STATE_LABEL.passed} ·{' '}
          {progress.passedBy === 'placement' ? 'Placement' : MASTERY_DISPLAY[progress.tier].label}
        </span>
      )
    case 'in_progress':
      return (
        <span className={`${base} bg-brand/15 text-brand-ink`}>{PATH_STATE_LABEL.in_progress}</span>
      )
    case 'locked':
      return (
        <span className={`${base} bg-muted text-muted-foreground`}>{PATH_STATE_LABEL.locked}</span>
      )
    default:
      // Behind a pending first step the path's opening step comes after it,
      // so it says "Then" (as the Study Plan's lead row does): one place to
      // start per page. Only an untouched step reaches here; a step picked by
      // leverage or already underway is in_progress and keeps its own badge.
      return isNext ? (
        <span className={`${base} bg-brand/15 text-brand-ink`}>
          <Sparkles className="h-3 w-3" />
          {firstStepPending ? 'Then' : 'Start here'}
        </span>
      ) : null
  }
}

function StepCard({
  step,
  n,
  progress,
  isNext,
  hub,
  moduleNames,
  firstStepPending,
  allSteps,
  signedIn,
}: {
  step: LearningPathStep
  /** Every step's progress — for what a placement pass here would open, and a locked step's prerequisites. */
  allSteps: PathStepProgress[]
  /** Placement checks need an account; visitors never see the offer. */
  signedIn: boolean
  /** 1-based position within the track. */
  n: number
  progress: PathStepProgress
  /** True when this is the step "Continue" points at. */
  isNext: boolean
  /** A first step is showing above the path; see StateBadge. */
  firstStepPending: boolean
  /** Lesson metadata from the hub join (read time, difficulty, flashcards). */
  hub?: HubTopic
  /** Display names for the step's sub-topic lessons, by slug. */
  moduleNames: Map<string, string>
}) {
  const video = videoFor(step.topic)
  const gatePct = gateThresholdPct(step.gate)
  const gateLabel = MASTERY_DISPLAY[step.gate].label
  const accent =
    progress.state === 'passed'
      ? TIER_ACCENT[progress.tier]
      : progress.state === 'in_progress' || isNext
        ? 'border-brand/50'
        : progress.state === 'locked'
          ? 'border-border/60'
          : 'border-border'

  let statusLine: string | null = null
  if (progress.state === 'passed' && progress.passedBy === 'placement') {
    const p = progress.placement.passed
    statusLine = p
      ? `Placed out: ${p.correct} of ${p.total} on fresh questions · a skill check earns the medal`
      : 'Passed by placement'
  } else if (progress.state === 'passed') {
    statusLine = `${progress.accuracyPct}% over ${progress.attempted} questions`
  } else if (progress.state === 'in_progress') {
    if (progress.attempted > 0) {
      const remaining = step.checkpoint.count - progress.attempted
      statusLine =
        remaining > 0
          ? `${progress.accuracyPct}% over ${progress.attempted} · ${remaining} more answer${remaining === 1 ? '' : 's'} to earn a tier`
          : `${progress.accuracyPct}% over ${progress.attempted} · reach ${gatePct}% for ${gateLabel} to pass`
    } else {
      statusLine = 'Lesson read · skill check next'
    }
  } else if (progress.state === 'locked') {
    statusLine = `Unlocks after ${progress.missing.join(' and ')}`
  }

  const prereqOffers =
    progress.state === 'locked'
      ? progress.missing
          .map((topic) => allSteps.find((s) => s.topic === topic))
          .filter((s): s is PathStepProgress => !!s && s.placement.offered)
      : []

  const lessonLabel =
    progress.state === 'passed'
      ? 'Review lesson'
      : progress.state === 'in_progress'
        ? 'Continue'
        : progress.state === 'locked'
          ? 'Preview lesson'
          : 'Start lesson'

  return (
    <li
      className={`rounded-xl border bg-card/60 p-4 ${accent} ${progress.state === 'locked' ? 'opacity-80' : ''}`}
      data-state={progress.state}
      data-testid={`step-${step.lessonSlug}`}
    >
      <div className="flex gap-3">
        <StepBubble n={n} progress={progress} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <Link
              href={`/learn/${step.lessonSlug}`}
              className="text-sm font-semibold leading-tight text-foreground hover:text-brand-ink"
            >
              {step.topic}
            </Link>
            <StateBadge progress={progress} isNext={isNext} firstStepPending={firstStepPending} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{step.why}</p>

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-muted-foreground">
            {hub?.reviewedAt && <VerifiedBadge reviewedAt={hub.reviewedAt} compact />}
            {hub?.estimatedReadMinutes && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {hub.estimatedReadMinutes} min
              </span>
            )}
            {hub?.difficulty && (
              <span className={`rounded-full px-1.5 py-0.5 ${DIFFICULTY_COLOR[hub.difficulty]}`}>
                {DIFFICULTY_LABEL[hub.difficulty]}
              </span>
            )}
            {video && (
              <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                <PlayCircle className="h-3 w-3" />
                {video.duration} video
              </span>
            )}
            {hub && hub.flashcardCount > 0 && (
              <span className="inline-flex items-center gap-1">
                <Layers className="h-3 w-3" />
                {hub.flashcardCount} cards
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Flag className="h-3 w-3" />
              {step.checkpoint.count}-question skill check
            </span>
          </div>

          {step.modules.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
              <span className="text-muted-foreground">Go deeper:</span>
              {step.modules.map((slug) => (
                <Link
                  key={slug}
                  href={`/learn/${slug}`}
                  className="rounded-full border border-border bg-background px-2 py-0.5 text-foreground hover:border-brand/50 hover:text-brand-ink"
                >
                  {moduleNames.get(slug) ?? slug}
                </Link>
              ))}
            </div>
          )}

          {statusLine && (
            <div className="mt-2 text-[11px] text-muted-foreground" data-testid="status-line">
              {statusLine}
            </div>
          )}

          {/* A locked step's blocker can be proved from here too: "Know Linear
              Equations in 1 Variable already? Prove it". */}
          {signedIn && prereqOffers.length > 0 && (
            <div className="mt-1 flex flex-col gap-0.5 text-[11px] text-muted-foreground">
              {prereqOffers.map((p) => (
                <span key={p.topic}>
                  Know {p.topic} already?{' '}
                  <ProveItButton
                    topic={p.topic}
                    placement={p.placement}
                    unlocks={unlocksIfPassed(p.topic, allSteps)}
                    checkpointCount={step.checkpoint.count}
                    gatePct={gatePct}
                    variant="link"
                    label="Prove it"
                  />
                </span>
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              asChild
              size="sm"
              variant={progress.state === 'locked' ? 'outline' : 'default'}
              className={
                progress.state === 'locked' ? '' : 'bg-brand text-on-brand hover:bg-brand-hover'
              }
            >
              <Link href={`/learn/${step.lessonSlug}`}>
                <BookOpen className="mr-1.5 h-3.5 w-3.5" />
                {lessonLabel}
              </Link>
            </Button>
            {progress.state !== 'locked' && (
              <Button asChild size="sm" variant="outline">
                <Link
                  href={checkpointHref(step.topic, step.checkpoint.count)}
                  onClick={() => trackPathEvent('checkpoint_start', step.topic)}
                >
                  {progress.state === 'passed' ? (
                    <>
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                      Practice again
                    </>
                  ) : (
                    <>
                      <Flag className="mr-1.5 h-3.5 w-3.5" />
                      Skill check
                    </>
                  )}
                </Link>
              </Button>
            )}
            {signedIn && progress.state !== 'passed' && (
              <ProveItButton
                topic={step.topic}
                placement={progress.placement}
                unlocks={unlocksIfPassed(step.topic, allSteps)}
                checkpointCount={step.checkpoint.count}
                gatePct={gatePct}
              />
            )}
          </div>
        </div>
      </div>
    </li>
  )
}

/* ------------------------------------------------------------------ */
/*  Track                                                              */
/* ------------------------------------------------------------------ */

/**
 * The track pill's lead word. Leverage and underway are real continuations and
 * keep their labels; the path's opening step yields to a pending first step
 * ("Then", not a second "Start with").
 */
function nextLabel(next: PathNext, firstStepPending: boolean): string {
  switch (next.reason) {
    case 'leverage':
      return 'Highest leverage'
    case 'in_progress':
      return 'Next up'
    default:
      return firstStepPending ? 'Then' : 'Start with'
  }
}

function TrackBlock({
  track,
  progress,
  showProgress,
  matches,
  topicsBySlug,
  moduleNames,
  firstStepPending,
}: {
  track: LearningPathTrack
  progress: PathProgress
  /** True when authenticated — shows the passed counter + bar. */
  showProgress: boolean
  /** Search/section filter — a step renders only when this returns true. */
  matches: (step: LearningPathStep) => boolean
  topicsBySlug: Map<string, HubTopic>
  moduleNames: Map<string, string>
  /** A first step is showing above the path, so the path's opening step says "Then". */
  firstStepPending: boolean
}) {
  const summary = progress.tracks.find((t) => t.section === track.section)
  const byTopic = useMemo(
    () => new Map(progress.steps.map((s) => [s.topic, s] as const)),
    [progress.steps],
  )
  const pct = summary && summary.total > 0 ? Math.round((summary.passed / summary.total) * 100) : 0

  let n = 0
  const domains = track.domains
    .map((d) => {
      const steps = d.steps.map((step) => ({ step, n: ++n })).filter(({ step }) => matches(step))
      return { domain: d.domain, steps }
    })
    .filter((d) => d.steps.length > 0)
  if (domains.length === 0) return null

  return (
    <section aria-labelledby={`track-${track.section}`}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id={`track-${track.section}`} className="text-xl font-semibold text-foreground">
            {track.title}
          </h2>
          {showProgress && summary && (
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <span>
                {summary.passed} of {summary.total} passed
              </span>
              <Progress value={pct} className="h-1.5 w-32" aria-label={`${pct}% passed`} />
            </div>
          )}
        </div>
        {summary?.next && (
          <Link
            href={`/learn/${summary.next.lessonSlug}`}
            className="group inline-flex items-center gap-1.5 rounded-full border border-brand/40 bg-brand/10 px-3 py-1 text-xs text-brand-ink hover:bg-brand/20"
          >
            <Target className="h-3 w-3" />
            {nextLabel(summary.next, firstStepPending)}: {summary.next.topic}
            <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>

      <div className="space-y-6">
        {domains.map((d) => (
          <div key={d.domain}>
            <div className="mb-2 flex flex-wrap items-baseline gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                {d.domain}
              </h3>
              <span className="text-[11px] text-muted-foreground">
                ~{DOMAIN_QUESTIONS_PER_TEST[d.domain]} questions per test
              </span>
            </div>
            <ol className="grid gap-3 lg:grid-cols-2">
              {d.steps.map(({ step, n }) => (
                <StepCard
                  key={step.topic}
                  step={step}
                  n={n}
                  progress={byTopic.get(step.topic)!}
                  isNext={progress.next?.topic === step.topic}
                  hub={topicsBySlug.get(step.lessonSlug)}
                  moduleNames={moduleNames}
                  firstStepPending={firstStepPending}
                  allSteps={progress.steps}
                  signedIn={showProgress}
                />
              ))}
            </ol>
          </div>
        ))}
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  Main client component                                              */
/* ------------------------------------------------------------------ */

export default function LearnClient({
  topics,
  isAuthenticated,
  isPaid = false,
  dueCount,
  lastReviewedSlug,
  progress,
  firstStep = null,
}: {
  /** Every lesson the hub knows about (steps AND sub-topic modules), for card metadata. */
  topics: HubTopic[]
  isAuthenticated: boolean
  isPaid?: boolean
  dueCount: number
  lastReviewedSlug: string | null
  progress: PathProgress
  /**
   * The signed-in student's first step (src/lib/onboarding/first-step.ts), or
   * null once they have a starting level (and for visitors).
   */
  firstStep?: FirstStep | null
}) {
  const searchParams = useSearchParams()
  const [sectionFilter, setSectionFilter] = useState<SectionFilter>('ALL')
  const [search, setSearch] = useState(searchParams.get('search') ?? '')

  useEffect(() => {
    const sp = searchParams.get('search')
    if (sp) setSearch(sp)
  }, [searchParams])

  const topicsBySlug = useMemo(() => {
    const m = new Map<string, HubTopic>()
    for (const t of topics) m.set(t.slug, t)
    return m
  }, [topics])

  // Sub-topic lessons render as "go deeper" chips under their step, by name.
  const moduleNames = useMemo(() => {
    const m = new Map<string, string>()
    for (const t of topics) m.set(t.slug, t.topic)
    return m
  }, [topics])

  const q = search.trim().toLowerCase()
  const matches = (step: LearningPathStep) => {
    if (sectionFilter !== 'ALL' && step.section !== sectionFilter) return false
    if (!q) return true
    if (step.topic.toLowerCase().includes(q) || step.domain.toLowerCase().includes(q)) return true
    return step.modules.some((slug) => (moduleNames.get(slug) ?? slug).toLowerCase().includes(q))
  }
  const visibleCount = LEARNING_PATH.flatMap((t) => t.domains.flatMap((d) => d.steps)).filter(
    matches,
  ).length

  const totalSteps = progress.steps.length
  const passedSteps = progress.steps.filter((s) => s.state === 'passed').length
  const totalLessons = topics.filter((t) => t.hasLesson).length

  const lastViewed = lastReviewedSlug ? topicsBySlug.get(lastReviewedSlug) : null

  // "Footprint" = any sign the student has started: answers or read lessons on
  // the path, a last-reviewed lesson, or flashcards due.
  const hasFootprint =
    !!lastViewed || dueCount > 0 || progress.steps.some((s) => s.attempted > 0 || s.read)
  // The /try teaser is for visitors without an account. A signed-in student
  // with no starting level gets their first step instead (the card above the
  // strip), the same one the dashboard leads with. This used to show every
  // brand-new Free student the teaser, whether or not they had taken a test.
  const showDiagnostic = !isAuthenticated && !hasFootprint

  // The Continue target: the path's own next step when it is genuinely a
  // continuation (underway, or leverage-picked); else the last lesson the
  // student was in; else the start of the path for a signed-in student, unless
  // their first step is showing (two places to start is what the first step
  // replaced).
  const next = progress.next
  const continueCard =
    next && next.reason !== 'first'
      ? {
          href: `/learn/${next.lessonSlug}`,
          title: `Continue: ${next.topic}`,
          sub:
            next.reason === 'leverage'
              ? 'Your highest-leverage step right now.'
              : 'Pick up where you left off.',
        }
      : lastViewed
        ? {
            href: `/learn/${lastViewed.slug}`,
            title: `Continue: ${lastViewed.topic}`,
            sub: 'Pick up where you left off.',
          }
        : next && (hasFootprint || isAuthenticated) && !firstStep
          ? {
              href: `/learn/${next.lessonSlug}`,
              title: `Start the path: ${next.topic}`,
              sub: 'Step 1 of the Math track.',
            }
          : null

  const reviewStripCard =
    isAuthenticated && dueCount > 0 ? (
      <Link
        key="review"
        href="/learn/review"
        className="group flex items-center gap-3 rounded-lg border border-emerald-500/40 bg-emerald-50 p-4 transition hover:border-emerald-500/70 hover:bg-emerald-50 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/30"
      >
        <CalendarClock className="h-7 w-7 text-emerald-400" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
            {dueCount} card{dueCount === 1 ? '' : 's'} due for review
          </div>
          <div className="text-xs text-emerald-700 dark:text-emerald-300/70">
            Spend 5 min keeping past lessons fresh.
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-emerald-700 transition group-hover:translate-x-0.5 dark:text-emerald-300" />
      </Link>
    ) : !isAuthenticated ? (
      <div
        key="signin"
        className="flex items-center gap-3 rounded-lg border border-brand/30 bg-brand/10 p-4 dark:bg-brand/15"
      >
        <Sparkles className="h-7 w-7 text-brand-ink" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-brand-ink">Sign in to track progress</div>
          <div className="text-xs text-brand-ink/70">
            Your place on the path, skill checks and flashcards come with a free account.
          </div>
        </div>
        <Link
          href="/signin"
          className="rounded-md border border-brand/50 bg-brand/10 px-3 py-1.5 text-xs font-medium text-brand-ink hover:bg-brand/20 dark:text-brand-ink"
        >
          Sign in
        </Link>
      </div>
    ) : null

  const rightStripCard = continueCard ? (
    <Link
      key="continue"
      href={continueCard.href}
      className="group flex items-center gap-3 rounded-lg border border-border bg-card/60 p-4 transition hover:border-brand/50 hover:bg-card"
    >
      <RotateCcw className="h-7 w-7 text-brand-ink" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-foreground">{continueCard.title}</div>
        <div className="text-xs text-muted-foreground">{continueCard.sub}</div>
      </div>
      <ArrowRight className="h-4 w-4 text-brand-ink transition group-hover:translate-x-0.5 dark:text-brand-ink" />
    </Link>
  ) : showDiagnostic ? (
    <Link
      key="diagnostic"
      href="/try"
      className="group flex items-center gap-3 rounded-lg border border-border bg-card/60 p-4 transition hover:border-brand/50 hover:bg-card"
    >
      <Sparkles className="h-7 w-7 text-brand-ink" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-foreground">Take a 25-minute diagnostic</div>
        <div className="text-xs text-muted-foreground">
          Find your weakest topics so you know where to focus.
        </div>
      </div>
      <ArrowRight className="h-4 w-4 text-brand-ink transition group-hover:translate-x-0.5 dark:text-brand-ink" />
    </Link>
  ) : null

  const stripCards = [reviewStripCard, rightStripCard].filter(Boolean)

  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto max-w-6xl px-4 py-8">
        {/* Hero */}
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-foreground">Your SAT learning path</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {totalSteps} steps in two tracks. Each one is a lesson, worked examples, and a
            ten-question skill check — reach Silver and the next step opens.
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
            <span className="rounded-full border border-border bg-card/60 px-2.5 py-1">
              {totalSteps} steps
            </span>
            <span className="rounded-full border border-border bg-card/60 px-2.5 py-1">
              {totalLessons} lesson{totalLessons === 1 ? '' : 's'}
            </span>
            <span className="rounded-full border border-border bg-card/60 px-2.5 py-1">
              Skill checks from our question bank
            </span>
            {isAuthenticated && (
              <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-emerald-700 dark:text-emerald-300">
                {passedSteps} of {totalSteps} passed
              </span>
            )}
            {progress.personalised && (
              <span className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand/10 px-2.5 py-1 text-brand-ink">
                <Target className="h-3 w-3" />
                Next step chosen by leverage
              </span>
            )}
            {/* The free/Pro line: the path and its skill checks are free; picking
                the next step by leverage is the Pro intelligence. Offered, never
                shown as a lock. */}
            {isAuthenticated && !isPaid && (
              <Link
                href="/pricing"
                className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand/10 px-2.5 py-1 text-brand-ink hover:bg-brand/20"
              >
                <Target className="h-3 w-3" />
                Pro picks your next step by leverage
                <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </div>
        </header>

        <FirstStepCard step={firstStep} className="mb-6" />

        {/* Auth-aware strip — adaptive: 0, 1, or 2 cards, so it collapses
            instead of showing filler. */}
        {stripCards.length > 0 && (
          <div className={`mb-6 grid gap-3 ${stripCards.length > 1 ? 'md:grid-cols-2' : ''}`}>
            {stripCards}
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase text-muted-foreground">Track:</span>
            {(['ALL', 'MATH', 'RW'] as const).map((s) => (
              <Button
                key={s}
                size="sm"
                variant={sectionFilter === s ? 'default' : 'outline'}
                className={
                  sectionFilter === s
                    ? 'bg-brand text-on-brand hover:bg-brand-hover'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }
                onClick={() => setSectionFilter(s)}
              >
                {s === 'ALL' ? 'Both' : s === 'RW' ? 'Reading & Writing' : 'Math'}
              </Button>
            ))}
          </div>

          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search steps…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-border bg-card pl-8"
            />
          </div>

          <span className="text-xs text-muted-foreground">
            {visibleCount} step{visibleCount === 1 ? '' : 's'}
          </span>
        </div>

        {visibleCount === 0 ? (
          <p className="text-muted-foreground">No steps match your search. Try a different term.</p>
        ) : (
          <div className="space-y-10">
            {LEARNING_PATH.map((track) => (
              <TrackBlock
                key={track.section}
                track={track}
                progress={progress}
                showProgress={isAuthenticated}
                matches={matches}
                topicsBySlug={topicsBySlug}
                moduleNames={moduleNames}
                firstStepPending={firstStep !== null}
              />
            ))}
          </div>
        )}
      </div>
    </main>
  )
}
